<form [formGroup]="formData" (ngSubmit)="onSubmit()" class="signup-form">
  <section class="signup-shell">
    <div class="signup-card">

      <!-- Brand header -->
      <header class="signup-header">
        <span class="signup-logo-ring">
          <img alt="MyMedsPH" class="signup-logo" src="assets/favicons/android-chrome-192x192.png">
        </span>
        <div class="signup-heading">
          <h1 class="signup-title">Dental App Account Registration</h1>
          <p class="signup-subtitle">Create your MyMedsPH practice account in a few quick steps.</p>
        </div>
      </header>

      <!-- Step progress -->
      <nav class="signup-steps" aria-label="Registration progress">
        <ol class="signup-steps-list">
          <li class="signup-step"
              [class.is-active]="page === PAGE_PERSONAL_INFO"
              [class.is-complete]="page > PAGE_PERSONAL_INFO">
            <span class="signup-step-marker">
              <i class="fas fa-check" *ngIf="page > PAGE_PERSONAL_INFO" aria-hidden="true"></i>
              <span *ngIf="page <= PAGE_PERSONAL_INFO">1</span>
            </span>
            <span class="signup-step-text">
              <span class="signup-step-title">Your details</span>
              <span class="signup-step-hint">Personal &amp; clinic</span>
            </span>
          </li>
          <li class="signup-step"
              [class.is-active]="page === PAGE_BILLING_INFO"
              [class.is-complete]="page > PAGE_BILLING_INFO">
            <span class="signup-step-marker">
              <i class="fas fa-check" *ngIf="page > PAGE_BILLING_INFO" aria-hidden="true"></i>
              <span *ngIf="page <= PAGE_BILLING_INFO">2</span>
            </span>
            <span class="signup-step-text">
              <span class="signup-step-title">Billing</span>
              <span class="signup-step-hint">Invoice details</span>
            </span>
          </li>
          <li class="signup-step"
              [class.is-active]="page === PAGE_SUBSCRIPTION">
            <span class="signup-step-marker"><span>3</span></span>
            <span class="signup-step-text">
              <span class="signup-step-title">Confirm</span>
              <span class="signup-step-hint">Review &amp; join</span>
            </span>
          </li>
        </ol>
      </nav>

      <!-- Loading indicator for retained data -->
      <div *ngIf="isLoadingRetainedData" class="signup-alert signup-alert--info">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
        <span>Loading your registration data…</span>
      </div>

      <!-- Success message for retained data -->
      <div *ngIf="retainedData && !isLoadingRetainedData" class="signup-alert signup-alert--success">
        <i class="fa fa-check-circle" aria-hidden="true"></i>
        <span>Your registration data has been loaded successfully. You can now proceed with payment.</span>
      </div>

      <!-- Expired / invalid signup link: offer a one-click re-send -->
      <div *ngIf="showDeadLinkRecovery" class="signup-alert signup-alert--info signup-alert--actionable">
        <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
        <span class="signup-alert__text">
          {{ deadLinkMessage }}
          Your details are still saved — we can email a fresh link to
          <strong>{{ userEmail }}</strong> so you do not have to fill this in again.
        </span>
        <span class="signup-alert__actions">
          <button
            type="button"
            class="signup-btn signup-btn--primary signup-btn--sm"
            (click)="requestNewSignupLink()"
            [disabled]="isRequestingNewLink">
            <i *ngIf="isRequestingNewLink" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
            <i *ngIf="!isRequestingNewLink" class="fas fa-paper-plane" aria-hidden="true"></i>
            <span>{{ isRequestingNewLink ? 'Sending…' : 'Send me a new link' }}</span>
          </button>
          <button
            type="button"
            class="signup-btn signup-btn--ghost signup-btn--sm"
            (click)="dismissDeadLinkRecovery()"
            [disabled]="isRequestingNewLink">
            <span>Start over instead</span>
          </button>
        </span>
      </div>

      <div class="signup-body">

        <!-- STEP 1: Personal + Clinic info -->
        <div class="signup-page" *ngIf="page == PAGE_PERSONAL_INFO">
          <div class="signup-grid">
            <fieldset class="signup-fieldset" [formGroup]="personalInfo">
              <legend class="signup-legend"><i class="fas fa-user-md" aria-hidden="true"></i> Personal Info</legend>
              <div class="signup-field">
                <label>Firstname</label>
                <input type="text" formControlName="firstname" class="signup-input">
                <control-messages [submitted]="submitted" [control]="personalInfo.controls.firstname"></control-messages>
              </div>
              <div class="signup-field">
                <label>Lastname</label>
                <input type="text" formControlName="lastname" class="signup-input">
                <control-messages [submitted]="submitted" [control]="personalInfo.controls.lastname"></control-messages>
              </div>
              <div class="signup-field">
                <label>Email</label>
                <input type="email" formControlName="email" class="signup-input">
                <control-messages [submitted]="submitted" [control]="personalInfo.controls.email"></control-messages>
              </div>
              <div class="signup-field">
                <label>Mobile</label>
                <input type="text" formControlName="mobile" class="signup-input">
                <control-messages [submitted]="submitted" [control]="personalInfo.controls.mobile"></control-messages>
              </div>
              <div class="signup-field">
                <label>License No</label>
                <input type="text" formControlName="licenseNo" class="signup-input">
                <control-messages [submitted]="submitted" [control]="personalInfo.controls.licenseNo"></control-messages>
              </div>
              <div class="signup-field">
                <label>PTR No</label>
                <input type="text" formControlName="ptrNo" class="signup-input">
                <control-messages [submitted]="submitted" [control]="personalInfo.controls.ptrNo"></control-messages>
              </div>
            </fieldset>

            <fieldset class="signup-fieldset" [formGroup]="clinicDetails">
              <legend class="signup-legend"><i class="fas fa-clinic-medical" aria-hidden="true"></i> Clinic Info</legend>
              <div class="signup-field">
                <label>Clinic Name</label>
                <input type="text" formControlName="clinicName" class="signup-input">
                <control-messages [submitted]="submitted" [control]="clinicDetails.controls.clinicName"></control-messages>
              </div>
              <div class="signup-field">
                <label>Specialization</label>
                <input type="text" formControlName="clinicSpecialization" class="signup-input">
                <control-messages [submitted]="submitted" [control]="clinicDetails.controls.clinicSpecialization"></control-messages>
              </div>
              <div class="signup-field">
                <label>Contact</label>
                <input type="text" formControlName="clinicContact" class="signup-input">
                <control-messages [submitted]="submitted" [control]="clinicDetails.controls.clinicContact"></control-messages>
              </div>
              <div class="signup-field">
                <label>Address</label>
                <input type="text" formControlName="clinicAddress" class="signup-input">
                <control-messages [submitted]="submitted" [control]="clinicDetails.controls.clinicAddress"></control-messages>
              </div>
            </fieldset>
          </div>

          <div class="signup-actions">
            <a (click)="backToLogin()" class="signup-link"><i class="fas fa-arrow-left" aria-hidden="true"></i> Go to Login</a>
            <button class="signup-btn signup-btn--primary" (click)="next()" type="button"
                    [disabled]="personalInfo.invalid || clinicDetails.invalid">
              Next <i class="fas fa-arrow-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <!-- STEP 2: Billing info -->
        <div class="signup-page" *ngIf="page == PAGE_BILLING_INFO">
          <div class="signup-grid" [formGroup]="billingInfo">
            <fieldset class="signup-fieldset">
              <legend class="signup-legend"><i class="fas fa-file-invoice-dollar" aria-hidden="true"></i> Billing Info</legend>
              <div class="signup-field">
                <label>Billing Name</label>
                <input type="text" formControlName="billingName" class="signup-input">
                <control-messages [submitted]="submitted" [control]="billingInfo.controls.billingName"></control-messages>
              </div>
              <div class="signup-field">
                <label>Billing Email</label>
                <input type="email" formControlName="billingEmail" class="signup-input">
                <control-messages [submitted]="submitted" [control]="billingInfo.controls.billingEmail"></control-messages>
              </div>
              <div class="signup-field">
                <label>Billing Contact</label>
                <input type="text" formControlName="billingPhone" class="signup-input">
                <control-messages [submitted]="submitted" [control]="billingInfo.controls.billingPhone"></control-messages>
              </div>
            </fieldset>

            <fieldset class="signup-fieldset">
              <legend class="signup-legend"><i class="fas fa-map-marker-alt" aria-hidden="true"></i> Billing Address</legend>
              <div class="signup-field">
                <label for="billingProvince">Province</label>
                <select class="signup-input signup-select" formControlName="billingProvince" name="billingProvince" id="billingProvince">
                  <option *ngFor="let p of provinceList" [value]="p">{{p}}</option>
                </select>
                <control-messages [submitted]="submitted" [control]="billingInfo.controls.billingProvince"></control-messages>
              </div>
              <div class="signup-field">
                <label for="billingCity">City</label>
                <select class="signup-input signup-select" formControlName="billingCity" name="billingCity" id="billingCity">
                  <option *ngFor="let c of provinces[billingInfo.value.billingProvince]" [value]="c">{{c}}</option>
                </select>
                <control-messages [submitted]="submitted" [control]="billingInfo.controls.billingCity"></control-messages>
              </div>
              <div class="signup-field">
                <label>Zip Code</label>
                <input type="text" formControlName="billingZip" class="signup-input">
                <control-messages [submitted]="submitted" [control]="billingInfo.controls.billingZip"></control-messages>
              </div>
              <div class="signup-field">
                <label>Street Address</label>
                <input type="text" formControlName="billingAddress" class="signup-input">
                <control-messages [submitted]="submitted" [control]="billingInfo.controls.billingAddress"></control-messages>
              </div>
            </fieldset>
          </div>

          <div class="signup-actions">
            <a (click)="previousPage()" class="signup-link"><i class="fas fa-arrow-left" aria-hidden="true"></i> Go Back</a>
            <button class="signup-btn signup-btn--primary" (click)="next()" type="button" [disabled]="billingInfo.invalid">
              Next <i class="fas fa-arrow-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <!-- STEP 3: Subscription / confirm -->
        <div class="signup-page" *ngIf="page == PAGE_SUBSCRIPTION">
          <div class="signup-plan">
            <div class="signup-plan-head">
              <div class="signup-plan-id">
                <span class="signup-plan-name">Basic Plan</span>
                <span class="signup-plan-tag">Everything you need to run your clinic</span>
              </div>
              <span class="signup-plan-badge"><i class="fas fa-check" aria-hidden="true"></i> Selected</span>
            </div>

            <div class="signup-plan-body">
              <div class="signup-plan-col">
                <h3 class="signup-plan-subtitle">With your subscription, you get</h3>
                <ul class="signup-feature-list">
                  <li>1 Account</li>
                  <li>1 Clinic</li>
                  <li>Unlimited associate records</li>
                  <li>Unlimited staff records</li>
                  <li>Unlimited patient records</li>
                  <li>Unlimited appointments</li>
                  <li>Unlimited recalls</li>
                  <li>Unlimited charts</li>
                  <li>Unlimited patient transactions</li>
                  <li>Up to 10GB cloud storage for uploads</li>
                </ul>
              </div>
              <div class="signup-plan-col signup-plan-col--addons">
                <h3 class="signup-plan-subtitle">Optional add-ons</h3>
                <ul class="signup-feature-list signup-feature-list--muted">
                  <li>Additional clinic @ ₱380.00 / month</li>
                  <li>Additional cloud storage for uploads (price will vary based on hosting provider)</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="signup-actions signup-actions--confirm">
            <a (click)="previousPage()" class="signup-link"><i class="fas fa-arrow-left" aria-hidden="true"></i> Go back</a>
            <button class="signup-btn signup-btn--primary signup-btn--lg" [disabled]="submitted" type="submit">
              <i class="fas fa-check" *ngIf="!submitted" aria-hidden="true"></i>
              <i class="fas fa-spinner fa-spin" *ngIf="submitted" aria-hidden="true"></i>
              Register and Join MyMedsPH
            </button>
          </div>
        </div>

      </div>
    </div>
  </section>
</form>


<!-- Email Verification Modal -->
<div class="modal fade" [class.show]="showEmailVerificationModal" [style.display]="showEmailVerificationModal ? 'block' : 'none'" tabindex="-1" role="dialog" aria-labelledby="emailVerificationModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-scrollable modal-lg" role="document">
    <div class="modal-content verification-modal">
      <div class="modal-header verification-header">
        <div class="header-content">
          <div class="icon-wrapper">
            <i class="fas fa-envelope-open-text" aria-hidden="true"></i>
          </div>
          <h4 class="modal-title" id="emailVerificationModalLabel">Verify Your Email</h4>
        </div>
        <button type="button" class="close-btn" (click)="closeEmailVerificationModal()" aria-label="Close modal">
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>
      <div class="modal-body verification-body">
        <div class="email-info">
          <p class="email-text">
            We've sent a verification code to
            <span class="email-highlight">{{ userEmail }}</span> .
          </p>
          <p class="instruction-text">
            Please enter the 6-digit code below to continue.
          </p>
        </div>

        <form [formGroup]="verificationForm" (ngSubmit)="onVerifyEmail()" class="verification-form">
          <div class="code-input-container">
            <label class="code-label" for="verification-code-input">Verification Code</label>
            <input
              type="text"
              id="verification-code-input"
              class="code-input-single"
              formControlName="verification_code"
              placeholder="000000"
              maxlength="6"
              inputmode="numeric"
              pattern="[0-9]*"
              autocomplete="one-time-code"
              [class.is-invalid]="verificationForm.get('verification_code')?.invalid && verificationForm.get('verification_code')?.touched"
              aria-describedby="verification-code-error"
            >
            <div class="error-message" id="verification-code-error" *ngIf="verificationForm.get('verification_code')?.invalid && verificationForm.get('verification_code')?.touched">
              Please enter a valid 6-digit code
            </div>
          </div>

          <div class="action-buttons">
            <button
              type="submit"
              class="verify-btn"
              [disabled]="verificationSubmitted || verificationForm.invalid"
              [class.loading]="verificationSubmitted"
            >
              <div class="btn-content">
                <i class="fas fa-check-circle btn-icon" *ngIf="!verificationSubmitted"></i>
                <div class="spinner" *ngIf="verificationSubmitted"></div>
                <span class="btn-text">{{ verificationSubmitted ? 'Verifying...' : 'Verify Email' }}</span>
              </div>
            </button>
          </div>
        </form>

        <div class="resend-section">
          <p class="resend-text">Didn't receive the code?</p>
          <button
            type="button"
            class="resend-btn"
            (click)="resendVerificationCode()"
            [disabled]="resendCooldown > 0"
          >
            <i class="fas fa-redo-alt"></i>
            <span>{{ resendCooldown > 0 ? 'Resend in ' + resendCooldown + 's' : 'Resend Code' }}</span>
          </button>
        </div>

        <div class="timer-info">
          <i class="fas fa-clock"></i>
          <span>Code expires in 5 minutes</span>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Payment Modal -->
<div class="modal fade" [class.show]="showPaymentModal" [style.display]="showPaymentModal ? 'block' : 'none'" tabindex="-1" role="dialog" aria-labelledby="paymentModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-scrollable" role="document">
    <div class="modal-content">
      <div class="modal-header payment-modal-header text-dark">
        <h4 class="modal-title" id="paymentModalLabel">
          <i class="fas fa-credit-card mr-2" aria-hidden="true"></i>
           Onboarding Fee
        </h4>

      </div>
      <div class="modal-body onboarding-payment p-4">
        <div class="onboarding-payment-success">
          <div class="onboarding-payment-success-icon" aria-hidden="true">
            <i class="fas fa-check"></i>
          </div>
          <h5 class="onboarding-payment-success-title">Email verified!</h5>
          <p class="onboarding-payment-success-lead text-muted">
            Complete your registration by paying the onboarding fee.
          </p>
        </div>

        <div class="onboarding-payment-callout" role="note">
          <i class="fas fa-envelope-open-text onboarding-payment-callout-icon" aria-hidden="true"></i>
          <div class="onboarding-payment-callout-text">
            We’ve also sent a confirmation email with a secure link. If you want to continue later — or if you
            close this browser — open that email and finish registration anytime within the next
            <strong>24 hours</strong>. After that, the link expires and you can sign up again later.
          </div>
        </div>

        <div class="onboarding-payment-fees" role="region" aria-label="Onboarding fee breakdown">
          <div class="onboarding-payment-fees-row">
            <span class="onboarding-payment-fees-label">Onboarding fee</span>
            <span class="onboarding-payment-fees-value">₱{{ BASIC_ACTIVATION_AMOUNT | number:'1.0-0' }}</span>
          </div>
          <ng-container *ngIf="selectedGateway === 'xendit' && xenditFeeEnabled">
            <div class="onboarding-payment-fees-row">
              <span class="onboarding-payment-fees-label">Service fee</span>
              <span class="onboarding-payment-fees-value">₱{{ xenditFee | number:'1.2-2' }}</span>
            </div>
            <div class="onboarding-payment-fees-row onboarding-payment-fees-row--total">
              <span class="onboarding-payment-fees-label">Total due</span>
              <span class="onboarding-payment-fees-value">₱{{ (BASIC_ACTIVATION_AMOUNT + xenditFee) | number:'1.2-2' }}</span>
            </div>
          </ng-container>
          <p class="onboarding-payment-fees-footnote">
            Non-refundable. Covers account setup and onboarding.
          </p>
        </div>

        <p class="onboarding-payment-secure-note text-muted">
          <i class="fas fa-lock" aria-hidden="true"></i>
          You’ll be redirected to our secure payment gateway to finish the transaction.
        </p>

        <div
          class="onboarding-payment-agreement"
          [hidden]="!(selectedGateway === 'xendit' && xenditFeeEnabled)">
          <input
            type="checkbox"
            id="agreeNonRefundable"
            #agreeCheckbox>
          <label for="agreeNonRefundable">
            I agree that the service fee is non-refundable and that VAT is not included.
          </label>
        </div>
      </div>
      <div class="modal-footer onboarding-payment-footer">
        <button
          type="button"
          class="proceed-payment-btn"
          [disabled]="isProcessingPayment || (selectedGateway === 'xendit' && xenditFeeEnabled && !agreeCheckbox.checked)"
          (click)="proceedWithPayment()"
          aria-label="Proceed to payment gateway">
          <div class="btn-content">
            <i
              class="fas"
              [class.fa-credit-card]="!isProcessingPayment"
              [class.fa-spinner]="isProcessingPayment"
              [class.fa-spin]="isProcessingPayment"
              aria-hidden="true">
            </i>
            <span class="btn-text">
              {{ isProcessingPayment ? 'Processing...' : 'Proceed to Payment' }}
            </span>
          </div>
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Modal Backdrop -->
<div class="modal-backdrop fade" [class.show]="showEmailVerificationModal || showPaymentModal" [style.display]="(showEmailVerificationModal || showPaymentModal) ? 'block' : 'none'"></div>
